import './commandPalette.css'

import { Command } from 'cmdk'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { type SearchResult, useGlobalSearch } from './useGlobalSearch'

export function CommandPalette() {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const navigate = useNavigate()
  const { results, isLoading, error } = useGlobalSearch(search)

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault()
        setOpen((prev) => !prev)
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [])

  function handleSelect(result: SearchResult) {
    void navigate(result.route)
    setOpen(false)
    setSearch('')
  }

  function handleOpenChange(nextOpen: boolean) {
    setOpen(nextOpen)
    if (!nextOpen) setSearch('')
  }

  const grouped = useMemo(() => {
    const map = new Map<string, SearchResult[]>()
    for (const r of results) {
      const list = map.get(r.category) ?? []
      list.push(r)
      map.set(r.category, list)
    }
    return map
  }, [results])

  return (
    <Command.Dialog
      open={open}
      onOpenChange={handleOpenChange}
      shouldFilter={false}
      label="Global search"
    >
      <Command.Input
        value={search}
        onValueChange={setSearch}
        placeholder="Search by email, ID or IP address..."
      />
      <Command.List>
        {[...grouped.entries()].map(([category, items]) => (
          <Command.Group key={category} heading={category}>
            {items.map((result) => (
              <Command.Item
                key={result.id}
                value={result.id}
                onSelect={() => handleSelect(result)}
              >
                <span className="item-label">{result.label}</span>
                {result.label !== result.id && (
                  <span className="item-id">{result.id.slice(0, 8)}...</span>
                )}
                {result.description && (
                  <span className="cmdk-badge">{result.description}</span>
                )}
              </Command.Item>
            ))}
          </Command.Group>
        ))}

        {isLoading && <div className="cmdk-loading">Searching...</div>}

        {!isLoading && error && (
          <div className="cmdk-error" role="alert">
            Search failed: {error}
          </div>
        )}

        {!isLoading &&
          !error &&
          search.trim().length >= 2 &&
          results.length === 0 && (
            <Command.Empty>No results found</Command.Empty>
          )}
      </Command.List>
    </Command.Dialog>
  )
}
